import React from "react"
import { Button, CircularProgress, IconButton, Tooltip } from "@mui/material"
import {
    DeleteOutline,
    EditOutlined,
    PaymentsOutlined,
    ReceiptLongOutlined
} from "@mui/icons-material"
import { contactOf, initials } from "./helpers"

const buttonSx = { textTransform: "none", borderRadius: "8px", px: 1.25, whiteSpace: "nowrap" }

function Avatar({ student }) {
    const photo = student?.meta_data?.photo
    if (typeof photo === "string" && /^(https?:|data:image)/.test(photo)) {
        return <img src={photo} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
    }
    return (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
            {initials(student?.name)}
        </span>
    )
}

function RowActions({ student, busy, onReceipt, onFee, onEdit, onDelete }) {
    return (
        <div className="flex items-center justify-end gap-1.5">
            <Button
                size="small"
                variant="outlined"
                startIcon={busy ? <CircularProgress size={14} /> : <ReceiptLongOutlined fontSize="small" />}
                disabled={busy}
                onClick={() => onReceipt(student)}
                sx={buttonSx}
            >
                Receipt
            </Button>
            <Button
                size="small"
                variant="outlined"
                color="success"
                startIcon={<PaymentsOutlined fontSize="small" />}
                onClick={() => onFee(student)}
                sx={buttonSx}
            >
                Fee
            </Button>
            <Tooltip title="Edit student" arrow>
                <IconButton size="small" aria-label={`Edit ${student.name}`} onClick={() => onEdit(student)}>
                    <EditOutlined fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete student" arrow>
                <IconButton
                    size="small"
                    color="error"
                    aria-label={`Delete ${student.name}`}
                    onClick={() => onDelete(student)}
                >
                    <DeleteOutline fontSize="small" />
                </IconButton>
            </Tooltip>
        </div>
    )
}

/**
 * Students of one class: a table on wide screens and stacked cards on
 * phones. Every action receives the student it belongs to.
 */
export default function StudentList({ students, busyId, ...actions }) {
    return (
        <>
            {/* Wide screens */}
            <div className="hidden overflow-x-auto md:block">
                <table className="w-full min-w-[820px] text-left text-sm">
                    <thead>
                        <tr className="border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500">
                            <th className="w-20 py-3 pl-5 pr-2 font-semibold">Roll no</th>
                            <th className="px-3 py-3 font-semibold">Student</th>
                            <th className="px-3 py-3 font-semibold">DOB</th>
                            <th className="px-3 py-3 font-semibold">Father</th>
                            <th className="px-3 py-3 font-semibold">Mother</th>
                            <th className="px-3 py-3 font-semibold">Contact</th>
                            <th className="py-3 pl-3 pr-5 text-right font-semibold">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {students.map((student) => (
                            <tr key={student.id ?? student.roll_no} className="align-middle hover:bg-slate-50/70">
                                <td className="py-3 pl-5 pr-2">
                                    <span className="inline-flex min-w-[2rem] justify-center rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-slate-700">
                                        {student.roll_no ?? "-"}
                                    </span>
                                </td>
                                <td className="max-w-[260px] px-3 py-3">
                                    <div className="flex min-w-0 items-center gap-3">
                                        <Avatar student={student} />
                                        <div className="min-w-0">
                                            <div className="truncate font-medium text-slate-800">{student.name || "-"}</div>
                                            <div className="truncate text-xs text-slate-500">{student.email || "-"}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="whitespace-nowrap px-3 py-3 tabular-nums text-slate-700">
                                    {student.dob || <span className="text-slate-400">Not provided</span>}
                                </td>
                                <td className="max-w-[160px] truncate px-3 py-3 text-slate-700">{student.father_name || "-"}</td>
                                <td className="max-w-[160px] truncate px-3 py-3 text-slate-700">{student.mother_name || "-"}</td>
                                <td className="whitespace-nowrap px-3 py-3 tabular-nums text-slate-700">{contactOf(student) || "-"}</td>
                                <td className="py-3 pl-3 pr-5">
                                    <RowActions student={student} busy={busyId === student.id} {...actions} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Phones */}
            <ul className="divide-y divide-slate-100 md:hidden">
                {students.map((student) => (
                    <li key={student.id ?? student.roll_no} className="space-y-3 px-4 py-4">
                        <div className="flex items-start gap-3">
                            <Avatar student={student} />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="truncate font-medium text-slate-800">{student.name || "-"}</div>
                                    <span className="shrink-0 rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-slate-700">
                                        Roll {student.roll_no ?? "-"}
                                    </span>
                                </div>
                                <div className="truncate text-xs text-slate-500">{student.email || "-"}</div>
                            </div>
                        </div>
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                            <div>
                                <dt className="text-slate-400">Father</dt>
                                <dd className="truncate text-slate-700">{student.father_name || "-"}</dd>
                            </div>
                            <div>
                                <dt className="text-slate-400">Mother</dt>
                                <dd className="truncate text-slate-700">{student.mother_name || "-"}</dd>
                            </div>
                            <div>
                                <dt className="text-slate-400">DOB</dt>
                                <dd className="truncate tabular-nums text-slate-700">{student.dob || "Not provided"}</dd>
                            </div>
                            <div>
                                <dt className="text-slate-400">Contact</dt>
                                <dd className="truncate tabular-nums text-slate-700">{contactOf(student) || "-"}</dd>
                            </div>
                        </dl>
                        <RowActions student={student} busy={busyId === student.id} {...actions} />
                    </li>
                ))}
            </ul>
        </>
    )
}
